import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { toast } from '@spartan-ng/brain/sonner';
import { Apollo } from 'apollo-angular';
import { environment } from 'environments/environment';
import { catchError, finalize, map, Observable, of, switchMap } from 'rxjs';
import { oAuthCreateImpersonateToken } from '../../data-access/authentication.graphql';
import { AuthenticationService } from '../authentication.service';
import type {
  SignInOutcome,
  TwoFactorEnrollment,
  TwoFactorProof,
  TwoFactorStep,
} from '../authentication.types';
import { LandingResolverService } from '../landing-resolver.service';
import { SessionRecoveryCoordinatorService } from '../session-recovery-coordinator.service';
import { TokenStore } from '../token-store.service';

/** Response shape of `oAuthCreateImpersonateToken` — deliberately no `refreshToken` (design D4). */
interface OAuthImpersonateTokens {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
}

@Injectable()
export class AuthenticationAuroraAdapterService extends AuthenticationService {
  private readonly apollo = inject(Apollo);
  private readonly http = inject(HttpClient);
  private readonly tokenStore = inject(TokenStore);
  private readonly landingResolver = inject(LandingResolverService);
  private readonly transloco = inject(TranslocoService);
  private readonly coordinator = inject(SessionRecoveryCoordinatorService);

  /**
   * Creates the hub session: `POST /o-auth/sign-in` answers 204 and sets the `hub_session` cookie
   * (hence `withCredentials`), or 200 `{ step, challenge }` when a second factor is still needed
   * (no cookie yet). It issues no token and never touches `TokenStore` — the tokens arrive when
   * `/auth/callback` redeems the code the next `/authorize` produces. A rejection surfaces as the
   * `HttpErrorResponse` (400 `invalid_grant`) for the form to render inline.
   */
  signIn(credentials: { email: string; password: string }): Observable<SignInOutcome> {
    return this.http
      .post<{ step: TwoFactorStep; challenge: string } | null>(
        `${environment.api.rest}/o-auth/sign-in`,
        { username: credentials.email, password: credentials.password },
        { withCredentials: true },
      )
      .pipe(
        map((body): SignInOutcome =>
          body?.challenge
            ? { status: 'second-factor', step: body.step, challenge: body.challenge }
            : { status: 'signed-in' },
        ),
      );
  }

  /** 204 sets the `hub_session` cookie; a refusal (`invalid_code`, `two_factor_locked`, `invalid_challenge`) surfaces as the `HttpErrorResponse`. */
  verifySecondFactor(challenge: string, proof: TwoFactorProof): Observable<boolean> {
    return this.http
      .post(
        `${environment.api.rest}/o-auth/sign-in/two-factor`,
        { challenge, ...proof },
        { withCredentials: true },
      )
      .pipe(map(() => true));
  }

  startTwoFactorEnrollment(challenge: string): Observable<TwoFactorEnrollment> {
    return this.http
      .post<TwoFactorEnrollment>(
        `${environment.api.rest}/o-auth/sign-in/two-factor/enrollment`,
        { challenge },
        { withCredentials: true },
      )
      .pipe(map(({ otpauthUri, secret }) => ({ otpauthUri, secret })));
  }

  /** On success the answer also sets the `hub_session` cookie; the recovery codes are shown once. */
  confirmTwoFactorEnrollment(challenge: string, code: string): Observable<string[]> {
    return this.http
      .post<{ recoveryCodes: string[] }>(
        `${environment.api.rest}/o-auth/sign-in/two-factor/enrollment/confirmation`,
        { challenge, code },
        { withCredentials: true },
      )
      .pipe(map(({ recoveryCodes }) => recoveryCodes));
  }

  signOut(): Observable<boolean> {
    // Signing out DURING an impersonated session must revoke the OPERATOR's refresh token: the
    // active impersonation credential has none (design D4), and the operator's own token is
    // reachable only through the stash that `clear()` is about to delete — so reading
    // `refreshToken()` straight off the active credential would silently skip the revocation and
    // leave a session the user believes they ended valid until it expires. Unwinding the stack
    // first keeps ONE revocation path instead of two. The impersonation token itself is revoked as
    // an access token (RFC 7009), since it has no refresh token to be revoked through.
    if (this.tokenStore.isImpersonating()) {
      this.coordinator.revokeAccessToken(this.tokenStore.accessToken());
    }
    if (this.tokenStore.isImpersonating() && this.tokenStore.hasStashedSession()) {
      this.tokenStore.endImpersonation();
    }

    const refreshToken = this.tokenStore.refreshToken();

    // No session to revoke server-side — just clear locally.
    if (!refreshToken) {
      this.tokenStore.clear();
      return of(true);
    }

    // Best-effort server-side revocation (RFC 7009), AWAITED so the caller's next navigation does
    // not abort it. The refresh token is read BEFORE clearing. Local credentials are cleared
    // regardless of the outcome (finalize) so a network/server failure never leaves the user in a
    // logged-in client state.
    return this.coordinator.revoke(refreshToken).pipe(
      catchError(() => of(null)),
      finalize(() => this.tokenStore.clear()),
      map(() => true),
    );
  }

  signUp(user: { name: string; email: string; password: string }): Observable<boolean> {
    // TODO: implement with actual mutation
    return of(true);
  }

  /** Always answers 200 `true`, whether or not the email exists; the emailed link is built server-side. */
  forgotPassword(email: string): Observable<boolean> {
    return this.http
      .post(`${environment.api.rest}/iam/user/forgot-password`, { email })
      .pipe(map(() => true));
  }

  /** A refused token surfaces as an `HttpErrorResponse` whose body carries the `translation` key. */
  resetPassword(password: string, token: string): Observable<boolean> {
    return this.http
      .post(`${environment.api.rest}/iam/user/reset-password`, { token, password })
      .pipe(map(() => true));
  }

  /**
   * Delegates every recovery decision — refresh, unwind, terminate — to the session recovery
   * coordinator's single flight (authentication delta spec, "AuthenticationService is purely
   * behavioral"). Never decides, performs a refresh/unwind/clear, or navigates itself: the
   * coordinator's `recoverForGuard()` already guarantees no navigation, matching the requirement
   * that `check()` (run inside `canActivate`) must not cancel the navigation the guard is
   * resolving.
   */
  check(): Observable<boolean> {
    return this.coordinator.recoverForGuard();
  }

  impersonate(accountId: string): Observable<boolean> {
    return this.apollo
      .mutate<{ oAuthCreateImpersonateToken: OAuthImpersonateTokens }>({
        mutation: oAuthCreateImpersonateToken,
        variables: { payload: { targetAccountId: accountId } },
      })
      .pipe(
        switchMap((response) => {
          const next = response.data?.oAuthCreateImpersonateToken;
          if (!next) return of(false);

          // Security finding F7: `beginImpersonation` can REFUSE the swap (an occupied origin
          // slot — a stale stash from a crashed tab, a second concurrent attempt, a hand-edited
          // localStorage entry). The mutation already succeeded server-side at this point, but
          // the ACTIVE credentials are still the operator's own token: re-materializing/navigating
          // regardless would make the operator believe they switched while every request still
          // carries their own privileges. Surface the refusal instead — no navigation, no reload
          // of session-scoped state, and a toast (this local refusal never touches the GraphQL
          // error link, so nothing else would tell the operator).
          const started = this.tokenStore.beginImpersonation({ accessToken: next.accessToken });
          if (!started) {
            toast.error(this.transloco.translate('navigation.Impersonation.SwapRefusedNotice'));
            return of(false);
          }

          this.reMaterializeSession();
          return of(true);
        }),
        // A veto refusal or network error must leave the operator's own session untouched.
        catchError(() => of(false)),
      );
  }

  stopImpersonation(): Observable<boolean> {
    // Reports whether the rollback ACTUALLY happened, mirroring `beginImpersonation`'s boolean
    // (security finding F7 / C10). `endImpersonation()` is a silent no-op with an empty stash, so a
    // hardcoded `true` made every caller announce a return to the operator's own session while the
    // active credential was still the impersonation token and the indicator was still visible.
    // Both conditions are required: a stale stash behind the operator's OWN (non-`act`) token must
    // neither revoke that live token nor restore the stale stash over it.
    if (!this.tokenStore.isImpersonating() || !this.tokenStore.hasStashedSession()) {
      return of(false);
    }

    // The impersonation token has no refresh token, so it is revoked as an ACCESS token (RFC 7009)
    // BEFORE the swap reads it away. Fired, never awaited: the rollback is a local credential swap
    // that must not depend on the network.
    this.coordinator.revokeAccessToken(this.tokenStore.accessToken());
    this.tokenStore.endImpersonation();
    this.reMaterializeSession();
    return of(true);
  }

  /**
   * Re-materializes session-scoped state after a credential swap (start or end of an
   * impersonation) by delegating to `LandingResolverService`, which reloads the current account,
   * applies the NEW session's role UI preferences (capability `frontend-role-ui-preferences`),
   * and navigates — instead of a hardcoded `/`. Neither transition carries an explicit redirect of
   * its own, so `apply(null)` lets the resolver fall through to the role preference, then `/`.
   *
   * The resolver OWNS the re-arm of `CurrentAccountService`'s `loaded` latch — `apply()` calls
   * `load()` itself, FIRST, before waiting (see `landing-resolver.service.ts`). This adapter must
   * NOT also call `load()`: a second, uncoordinated call here would race the resolver's own
   * re-arm and could leave a stale `loaded=true` visible for a tick before the resolver's own
   * re-arm lands.
   */
  private reMaterializeSession(): void {
    this.landingResolver.apply(null);
  }
}
